import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Bauteil, BauteilEigenschaft, ElektrolisteStandardtexte } from '../types';
import { bauteilService, elektrolisteService } from '../services/elektrolisteService';
import { useAuth } from '../contexts/AuthContext';
import { Icon, PageHeader, LoadingScreen, EmptyState, FilterStand, FilterZuruecksetzen, AnlegenButton, filterAktiv, fehlerText } from '../components/ui';
import { EigenschaftenEditor, bekannteSchluessel } from '../components/EigenschaftenEditor';
import { useStueckweise } from '../utils/stueckweise';
import { markiereNeu, ankunft, frischerIndex } from '../utils/ankunft';

const leeresFormular = { titel: '', eigenschaften: [] as BauteilEigenschaft[] };

/** Bauteil-Katalog für Elektrolisten, dazu (für Admins) die Standardtexte neuer Listen. */
const Bauteile: React.FC = () => {
  const { isAdmin } = useAuth();
  const [bauteile, setBauteile] = useState<Bauteil[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Bauteil | null>(null);
  const [formData, setFormData] = useState(leeresFormular);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [standardtexte, setStandardtexte] = useState<ElektrolisteStandardtexte | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => { laden(); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const laden = async () => {
    try {
      setBauteile(await bauteilService.getAll());
    } catch (err) { toast.error(fehlerText(err, 'Bauteile konnten nicht geladen werden')); }
    finally { setLoading(false); }
  };

  const vorschlaege = useMemo(() => bekannteSchluessel(bauteile.map(b => b.eigenschaften)), [bauteile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editing) {
        await bauteilService.update(editing.id, formData);
        markiereNeu(`bauteil-${editing.id}`);
        toast.success('Bauteil gespeichert');
      } else {
        const angelegt = await bauteilService.create(formData);
        markiereNeu(`bauteil-${angelegt.id}`);
        toast.success('Bauteil angelegt');
      }
      resetForm();
      await laden();
    } catch (err) {
      toast.error(fehlerText(err, 'Bauteil konnte nicht gespeichert werden'));
    }
  };

  const handleEdit = (bauteil: Bauteil) => {
    setOpenMenuId(null);
    setEditing(bauteil);
    setFormData({ titel: bauteil.titel, eigenschaften: bauteil.eigenschaften });
    setShowForm(true);
  };

  const handleNeu = () => {
    // Neue Bauteile starten mit den Spalten, die die Liste schon kennt – meist füllt man nur die Werte aus.
    setFormData({ titel: '', eigenschaften: vorschlaege.slice(0, 6).map(schluessel => ({ schluessel, wert: '' })) });
    setShowForm(true);
  };

  const handleDelete = async (bauteil: Bauteil) => {
    setOpenMenuId(null);
    if (window.confirm(`Bauteil „${bauteil.titel}“ löschen?`)) {
      try {
        await bauteilService.delete(bauteil.id);
        toast.success('Bauteil gelöscht');
        await laden();
      } catch (err) {
        toast.error(fehlerText(err, 'Bauteil konnte nicht gelöscht werden'));
      }
    }
  };

  const resetForm = () => {
    setFormData(leeresFormular);
    setEditing(null);
    setShowForm(false);
  };

  const oeffneStandardtexte = async () => {
    try {
      setStandardtexte(await elektrolisteService.getStandardtexte());
    } catch (err) {
      toast.error(fehlerText(err, 'Standardtexte konnten nicht geladen werden'));
    }
  };

  const speichereStandardtexte = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!standardtexte) return;
    try {
      await elektrolisteService.setStandardtexte(standardtexte);
      toast.success('Standardtexte gespeichert');
      setStandardtexte(null);
    } catch (err) {
      toast.error(fehlerText(err, 'Standardtexte konnten nicht gespeichert werden'));
    }
  };

  const gefiltert = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return bauteile.filter(b =>
      b.titel.toLowerCase().includes(q) || b.eigenschaften.some(e => e.wert.toLowerCase().includes(q))
    );
  }, [bauteile, searchTerm]);
  const { sichtbar, Ende, zeigeBis } = useStueckweise(gefiltert);
  // Aus der Suche angesprungen: auch weiter hinten liegende Bauteile rendern, damit sie aufleuchten.
  useEffect(() => {
    const i = frischerIndex(gefiltert, (b) => `bauteil-${b.id}`);
    if (i >= 0) zeigeBis(i);
  }, [gefiltert, zeigeBis]);
  const filterIstAktiv = searchTerm.trim() !== '';

  if (loading) return <LoadingScreen />;

  return (
    <div className="p-4 md:p-6">
      <div className="sticky top-0 z-10 bg-gray-100 pb-3 -mx-4 px-4 -mt-4 pt-4 md:-mx-6 md:px-6 md:-mt-6 md:pt-6">
        <PageHeader
          title="Elektrobauteile"
          actions={<>
            {isAdmin() && (
              <button
                type="button"
                onClick={oeffneStandardtexte}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Icon.Regler className="h-4 w-4" />Standardtexte
              </button>
            )}
            <AnlegenButton onClick={handleNeu}>Bauteil anlegen</AnlegenButton>
          </>}
          className="mb-3"
        />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="relative flex-1 min-w-[12rem]">
            <Icon.Search className={`pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 ${filterIstAktiv ? 'text-brand-600' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Bauteil suchen …"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full p-2 pl-8 border border-gray-300 rounded-lg text-sm ${filterIstAktiv ? filterAktiv : ''}`}
            />
          </div>
          <FilterStand treffer={gefiltert.length} gesamt={bauteile.length} einheit="Bauteile" aktiv={filterIstAktiv} onZuruecksetzen={() => setSearchTerm('')} />
        </div>
      </div>

      {showForm && (
        <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="ui-pop bg-white rounded-lg p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">{editing ? 'Bauteil bearbeiten' : 'Bauteil anlegen'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Titel</label>
                <input
                  type="text"
                  required
                  value={formData.titel}
                  onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="z. B. Poolscheinwerfer"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Eigenschaften</label>
                <p className="text-xs text-gray-400 mb-2">Jede Eigenschaft wird in der Elektroliste zu einer Spalte. Leere Zeilen werden nicht gespeichert.</p>
                <EigenschaftenEditor
                  eigenschaften={formData.eigenschaften}
                  onChange={(eigenschaften) => setFormData({ ...formData, eigenschaften })}
                  vorschlaege={vorschlaege}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={resetForm}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm">
                  Abbrechen
                </button>
                <button type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm">
                  {editing ? 'Speichern' : 'Bauteil anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {standardtexte && (
        <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="ui-pop bg-white rounded-lg p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1">Standardtexte für Elektrolisten</h2>
            <p className="text-sm text-gray-500 mb-4">Neue Elektrolisten starten mit diesen Texten. Bestehende Listen bleiben unverändert.</p>
            <form onSubmit={speichereStandardtexte}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Titel</label>
                <input
                  type="text"
                  required
                  value={standardtexte.titel}
                  onChange={(e) => setStandardtexte({ ...standardtexte, titel: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Text über der Tabelle</label>
                <textarea
                  rows={4}
                  value={standardtexte.einleitung}
                  onChange={(e) => setStandardtexte({ ...standardtexte, einleitung: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Text unter der Tabelle</label>
                <textarea
                  rows={2}
                  value={standardtexte.schlusstext}
                  onChange={(e) => setStandardtexte({ ...standardtexte, schlusstext: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setStandardtexte(null)}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm">
                  Abbrechen
                </button>
                <button type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm">
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {sichtbar.map((bauteil) => {
          const isMenuOpen = openMenuId === bauteil.id;
          const a = ankunft(`bauteil-${bauteil.id}`);
          const details = bauteil.eigenschaften.filter(e => e.wert).map(e => `${e.schluessel}: ${e.wert}`).join(' · ');

          return (
            <div key={bauteil.id} ref={a.ref} className={`bg-white rounded-lg shadow-sm border border-gray-100 ${a.className}`}>
              <div className="flex items-center p-3 md:p-4">
                <button type="button" onClick={() => handleEdit(bauteil)} className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-semibold text-gray-900 truncate">{bauteil.titel}</div>
                  {details && <div className="text-xs text-gray-500 truncate mt-0.5">{details}</div>}
                </button>

                <div className="relative flex-shrink-0" ref={isMenuOpen ? menuRef : undefined}>
                  <button
                    onClick={() => setOpenMenuId(isMenuOpen ? null : bauteil.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Weitere Aktionen"
                  >
                    <Icon.Mehr className="w-5 h-5" />
                  </button>
                  {isMenuOpen && (
                    <div className="ui-fade absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1">
                      <button
                        onClick={() => handleEdit(bauteil)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon.Edit className="w-4 h-4 text-gray-400" />
                        Bearbeiten
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={() => handleDelete(bauteil)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Icon.Trash className="w-4 h-4" />
                        Löschen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {Ende}
      </div>

      {gefiltert.length === 0 && (
        filterIstAktiv ? (
          <EmptyState
            icon={<Icon.Search className="h-6 w-6" />}
            title="Keine Bauteile gefunden"
            hint={`Kein Bauteil passt zu „${searchTerm}“.`}
            action={<FilterZuruecksetzen onClick={() => setSearchTerm('')} />}
          />
        ) : (
          <EmptyState
            icon={<Icon.Blitz className="h-6 w-6" />}
            title="Noch keine Bauteile"
            hint="Bauteile werden beim Kunden in Elektrolisten übernommen, etwa Scheinwerfer, Pumpen oder Steuerungen."
            action={<AnlegenButton onClick={handleNeu}>Bauteil anlegen</AnlegenButton>}
          />
        )
      )}
    </div>
  );
};

export default Bauteile;
